'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Check, X, Info } from 'lucide-react';

// ============================================================================
// CHIEDERE L'APP, E ARRIVARE PRONTI.
//
// Le app del telefono non stanno sui negozi pubblici e la versione Mac non
// esiste ancora: in tutti e tre i casi qualcuno di noi invita la persona a
// mano. Quel passaggio resta, di proposito. Quello che cambia e' come si
// chiede, e cosa si sa prima di chiedere.
//
// Prima era un `mailto:`. Sembra la cosa piu' semplice che ci sia, e invece e'
// la piu' fragile: su un telefono senza un programma di posta configurato non
// succede assolutamente niente, il tasto sembra rotto e la richiesta muore
// senza che nessuno dei due lo sappia. Quando funzionava, arrivava una mail
// vuota con scritto "Beta iOS" nell'oggetto: non si sapeva chi fosse, e
// soprattutto non si sapeva DOVE mandargli l'invito.
//
// Perche' e' quello il punto. L'invito di TestFlight lo manda Apple all'Apple
// ID, e quello di Play va all'account Google del telefono: quasi mai sono
// l'indirizzo di lavoro che una persona scrive d'istinto in un modulo. Un
// invito partito all'indirizzo sbagliato non torna indietro e non avvisa
// nessuno: resta semplicemente un cliente che aspetta. Quindi lo chiediamo, e
// lo chiediamo spiegando perche'.
//
// Il caso normale resta un clic: la casella "e' lo stesso indirizzo" parte
// spuntata, e chi ha davvero due indirizzi la toglie.
// ============================================================================

export type Piattaforma = 'ios' | 'android' | 'mac';

const CAMPO =
  'w-full rounded-[10px] border border-stone-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-[11px] text-[15px] outline-none transition-colors focus:border-[var(--accent)] placeholder:text-stone-400 dark:placeholder:text-zinc-600';

const ETICHETTA = 'text-[13px] font-medium text-stone-600 dark:text-zinc-400';

export function AppAccessDialog({
  platform,
  onClose,
}: {
  platform: Piattaforma | null;
  onClose: () => void;
}) {
  const t = useTranslations('AppAccess');
  const tDownload = useTranslations('download');
  const locale = useLocale();
  const [stato, setStato] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [stessoIndirizzo, setStessoIndirizzo] = useState(true);
  const primoCampo = useRef<HTMLInputElement>(null);

  // Riaprendolo per un'altra piattaforma si riparte puliti, se no chi ha appena
  // chiesto iOS trova la schermata "inviato" anche per Android.
  useEffect(() => {
    if (platform) {
      setStato('idle');
      setStessoIndirizzo(true);
      // Il fuoco sul primo campo: su desktop si scrive subito, senza cercare
      // dove cliccare.
      setTimeout(() => primoCampo.current?.focus(), 50);
    }
  }, [platform]);

  // Esc chiude. E' la prima cosa che prova chi si e' aperto il riquadro per
  // sbaglio, e non trovarla e' fastidioso.
  useEffect(() => {
    if (!platform) return;
    const chiudi = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', chiudi);
    return () => window.removeEventListener('keydown', chiudi);
  }, [platform, onClose]);

  if (!platform) return null;

  const telefono = platform !== 'mac';

  const nomePiattaforma =
    platform === 'ios'
      ? tDownload('ios.title')
      : platform === 'android'
        ? tDownload('android.title')
        : tDownload('mac.title');

  const daSapere =
    platform === 'ios' ? t('ready_ios') : platform === 'android' ? t('ready_android') : t('ready_mac');

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (stato === 'sending') return;
    setStato('sending');

    const dati = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      const res = await fetch('/api/app-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dati, platform, locale }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStato('sent');
    } catch {
      setStato('error');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-stone-950/60 backdrop-blur-[2px] p-0 sm:p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full sm:max-w-[460px] max-h-[92dvh] overflow-y-auto rounded-t-[20px] sm:rounded-[18px] bg-stone-50 dark:bg-zinc-950 border border-stone-200 dark:border-zinc-800 p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('title', { platform: nomePiattaforma })}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-[20px] font-bold tracking-[-0.02em] m-0">
            {t('title', { platform: nomePiattaforma })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 -mr-1 -mt-1 p-2 rounded-[8px] text-stone-400 hover:text-stone-950 dark:hover:text-zinc-50 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {stato === 'sent' ? (
          <div className="flex flex-col items-start gap-3 pt-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}
            >
              <Check size={22} />
            </div>
            <h3 className="text-[17px] font-bold m-0">{t('sentTitle')}</h3>
            <p className="text-[14px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">
              {t('sentBody')}
            </p>
          </div>
        ) : (
          <>
            <p className="text-[14px] leading-[1.55] text-stone-500 dark:text-zinc-400 mt-2 mb-4">
              {t('body')}
            </p>

            {/* Cosa sapere prima: chi legge questo arriva pronto, e l'invito non
                resta in attesa per una cosa che si poteva fare adesso. */}
            <div
              className="flex gap-2.5 rounded-[10px] p-3 mb-5 text-[13px] leading-[1.5]"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}
            >
              <Info size={16} className="shrink-0 mt-[1px]" />
              <span>{daSapere}</span>
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-3">
              {/* Campo esca: invisibile a chi legge, lo riempiono solo i bot. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute w-px h-px -left-[9999px] opacity-0"
              />

              <label className="flex flex-col gap-1.5">
                <span className={ETICHETTA}>{t('f_name')}</span>
                <input
                  ref={primoCampo}
                  name="name"
                  required
                  autoComplete="name"
                  placeholder={t('ph_name')}
                  className={CAMPO}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className={ETICHETTA}>{t('f_email')}</span>
                <input
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t('ph_email')}
                  className={CAMPO}
                />
              </label>

              {/* L'indirizzo dell'invito. Sul Mac non serve: non c'e' nessun
                  negozio di mezzo, c'e' solo una lista d'attesa. */}
              {telefono && (
                <div className="flex flex-col gap-2 rounded-[10px] border border-stone-200 dark:border-zinc-800 p-3">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stessoIndirizzo}
                      onChange={(e) => setStessoIndirizzo(e.target.checked)}
                      className="mt-[3px] h-4 w-4 accent-[var(--accent)]"
                    />
                    <span className="text-[13px] leading-[1.45] text-stone-600 dark:text-zinc-400">
                      {t('same')}
                      <span className="block text-[12px] text-stone-400 dark:text-zinc-500 mt-0.5">
                        {t('storeNote')}
                      </span>
                    </span>
                  </label>

                  {!stessoIndirizzo && (
                    <label className="flex flex-col gap-1.5">
                      <span className={ETICHETTA}>
                        {platform === 'ios' ? t('f_store_ios') : t('f_store_android')}
                      </span>
                      <input
                        name="storeEmail"
                        type="email"
                        autoComplete="off"
                        inputMode="email"
                        placeholder={t('ph_store')}
                        className={CAMPO}
                      />
                    </label>
                  )}
                </div>
              )}

              <label className="flex flex-col gap-1.5">
                <span className={ETICHETTA}>{t('f_company')}</span>
                <input
                  name="company"
                  autoComplete="organization"
                  placeholder={t('ph_company')}
                  className={CAMPO}
                />
              </label>

              {telefono && (
                <label className="flex flex-col gap-1.5">
                  <span className={ETICHETTA}>{t('f_seats')}</span>
                  <select name="seats" defaultValue="1" className={CAMPO}>
                    <option value="1">{t('seats1')}</option>
                    <option value="2-5">{t('seats5')}</option>
                    <option value="5+">{t('seatsMany')}</option>
                  </select>
                </label>
              )}

              <button
                type="submit"
                disabled={stato === 'sending'}
                className="mt-1 inline-flex items-center justify-center px-5 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 disabled:opacity-60 hover:opacity-90 transition-opacity"
              >
                {stato === 'sending' ? t('sending') : t('submit')}
              </button>

              {stato === 'error' && (
                <p className="text-[13px] text-red-600 dark:text-red-400 m-0">{t('errorBody')}</p>
              )}

              <p className="text-[12px] leading-[1.5] text-stone-400 dark:text-zinc-500 m-0">
                {t('privacy')}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
